import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Moon, Sun } from "lucide-react";
import Sidebar from "./Sidebar";
import NotificationBell from "../components/NotificationBell";
import GlobalSearch from "../components/GlobalSearch";
import { resolveMediaUrl } from "../utils/mediaUrl";
import { useTheme } from "../context/ThemeContext";

export default function Layout({ role, onLogout, children }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [desktopExpanded, setDesktopExpanded] = useState(
    () => localStorage.getItem("sidebarDefault") !== "collapsed"
  );

  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const toggleTheme = () => setTheme(isDark ? "light" : "dark");

  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    user = {};
  }

  const initials = (user.full_name || user.email || "U")
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const avatarUrl = resolveMediaUrl(user.avatar_url);

  return (
    <div className="flex h-screen items-stretch overflow-hidden bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <Sidebar
        role={role}
        onLogout={onLogout}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
        desktopExpanded={desktopExpanded}
        onDesktopToggle={() => setDesktopExpanded((expanded) => !expanded)}
      />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="sticky top-0 z-20 border-b border-slate-200 dark:border-slate-800/90 bg-white/95 dark:bg-slate-950/95 px-3 py-3 shadow-sm backdrop-blur sm:px-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation"
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 transition hover:bg-slate-100 dark:hover:bg-slate-800 lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>

            <div className="order-3 w-full sm:order-none sm:flex-1">
              <GlobalSearch />
            </div>

            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={toggleTheme}
                title={isDark ? "Switch to light mode" : "Switch to dark mode"}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 transition hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
              >
                {isDark ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-slate-600" />}
              </button>

              <NotificationBell />

              <button
                type="button"
                onClick={() => navigate("/profile")}
                title="My Profile"
                className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2.5 py-1.5 transition hover:bg-slate-100 dark:hover:bg-slate-800 sm:px-3"
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={user.full_name || "Profile"}
                    className="h-8 w-8 flex-shrink-0 rounded-full border border-slate-300 dark:border-slate-700 object-cover"
                  />
                ) : (
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700 text-xs font-bold text-slate-700 dark:text-white">
                    {initials}
                  </div>
                )}
                <div className="hidden min-w-0 text-left lg:block">
                  <div className="truncate text-xs font-semibold text-slate-900 dark:text-slate-100">
                    {user.full_name || "Profile"}
                  </div>
                  <div className="truncate text-[11px] text-slate-500 dark:text-slate-400 capitalize">
                    {({
                      admin: "Admin",
                      super_admin: "Admin",
                      supervisor: "HOD",
                      coordinator: "Coordinator",
                      service_coordinator: "Coordinator",
                      finance: "Finance",
                      finance_officer: "Finance",
                      employee: "Employee",
                      crm: "CRM",
                    })[user.role] || user.role || "Workspace"}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-950 p-3 sm:p-4 md:p-5 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
